// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { ChainIconName, ChainName, SimpleChainConfig } from './DeploymentConfigTypes'
import { ChainId } from './Enums'

export const NONPROD_SIMULATED_TRANSFER_WALLET_ADDR = '0xE91AC4D40666C2B189ed540E2135b0C97386680C'
export const NONPROD_SIMULATED_VAULT_ADDR = '0x6a294E587C7db1b466C7aE19c059adF073f3B684'
export const NONPROD_CALIBRATION_WALLET_ADDR = '0x4Cb5472d4e011Da12E94052C3D7C77f90EFEe13e'
export const NONPROD_FEE_VAULT_ADDR = '0xf6eA3f847CBed67A507106937bb7A8Fe3d196D61'
// PROVIDERS

const WALLET_CONNECT_PROJECT_ID = '95a7421b836a16c2c5b981ab3393a4ca'

const PLUME_DEFAULT_TESTNET_URL = 'https://testnet-rpc.plume.org/'
const PLUME_DEFAULT_MAINNET_URL = 'https://rpc.plume.org'
const SEPOLIA_DEFAULT_URL = 'https://eth-sepolia.g.alchemy.com/v2/ZfYMU4EmOzmizBu2ybqI8'
const FUJI_DEFAULT_URL = 'https://avax-fuji.g.alchemy.com/v2/4cSE9_-9aE5rv6QJtMm9U'

export const AVALANCHE_DEFAULT_URL = 'https://avax-mainnet.g.alchemy.com/v2/p5dgOkAqxryvxryB4To24'
const MAINNET_DEFAULT_URL = 'https://eth-mainnet.g.alchemy.com/v2/fcK4AEYquRkIgqn0REy8N0uOsiO3kqKI'

// WebSocket URLs
const PLUME_DEFAULT_TESTNET_WS_URL = 'wss://testnet-rpc.plume.org/'
const PLUME_DEFAULT_MAINNET_WS_URL = 'wss://rpc.plume.org'
const SEPOLIA_DEFAULT_WS_URL = 'wss://eth-sepolia.g.alchemy.com/v2/ZfYMU4EmOzmizBu2ybqI8'
const FUJI_DEFAULT_WS_URL = 'wss://avax-fuji.g.alchemy.com/v2/4cSE9_-9aE5rv6QJtMm9U'

export const AVALANCHE_DEFAULT_WS_URL = 'wss://avax-mainnet.g.alchemy.com/v2/p5dgOkAqxryvxryB4To24'
const MAINNET_DEFAULT_WS_URL = 'wss://eth-mainnet.g.alchemy.com/v2/fcK4AEYquRkIgqn0REy8N0uOsiO3kqKI'

const ETHERSCAN_FUJI = 'https://testnet.snowtrace.io/tx'
const ETHERSCAN_MAINNET = 'https://etherscan.io/tx'
const ETHERSCAN_SEPOLIA = 'https://sepolia.etherscan.io/tx'
const ETHERSCAN_AVALANCHE = 'https://snowtrace.io/tx'
const ETHERSCAN_PLUME_TESTNET = 'https://testnet-explorer.plume.org/tx'
const ETHERSCAN_PLUME_MAINNET = 'https://phoenix-explorer.plumenetwork.xyz/tx'

const BLOCK_EXPLORER_FUJI = 'https://testnet.snowtrace.io/'
const BLOCK_EXPLORER_MAINNET = 'https://etherscan.io/'
const BLOCK_EXPLORER_SEPOLIA = 'https://sepolia.etherscan.io/'
const BLOCK_EXPLORER_AVALANCHE = 'https://snowtrace.io/'
const BLOCK_EXPLORER_PLUME_TESTNET = 'https://testnet-explorer.plume.org/'
const BLOCK_EXPLORER_PLUME_MAINNET = 'https://phoenix-explorer.plumenetwork.xyz/'

export const chainIdToChainConfigurationValues = (
  chainId: ChainId
): {
  chainId: number
  chainName: ChainName
  etherscanUrl?: string
  blockExplorerUrl?: string
  chainIcon: ChainIconName

  // Network specific details
  nativeCurrency: {
    decimals: number
    name: string
    symbol: string
  }
  contracts?: any
  isTestnet: boolean
  frontendProviderUrl: string
  frontendWebSocketProviderUrl: string
  backendProviderUrl: string
  walletConnectProjectId: string
} => {
  if (chainId === ChainId.FUJI) {
    return {
      chainId: ChainId.FUJI,
      chainName: 'Fuji',
      blockExplorerUrl: BLOCK_EXPLORER_FUJI,
      etherscanUrl: ETHERSCAN_FUJI,
      chainIcon: 'fuji',
      nativeCurrency: {
        decimals: 18,
        name: 'Avalanche Fuji',
        symbol: 'AVAX',
      },
      contracts: {
        multicall3: {
          address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
          blockCreated: 7096959,
        },
      },
      isTestnet: true,
      frontendProviderUrl: FUJI_DEFAULT_URL,
      frontendWebSocketProviderUrl: FUJI_DEFAULT_WS_URL,
      backendProviderUrl: FUJI_DEFAULT_URL,
      walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
    }
  } else if (chainId === ChainId.SEPOLIA) {
    return {
      chainId: ChainId.SEPOLIA,
      chainName: 'Sepolia',
      blockExplorerUrl: BLOCK_EXPLORER_SEPOLIA,
      etherscanUrl: ETHERSCAN_SEPOLIA,
      frontendProviderUrl: SEPOLIA_DEFAULT_URL,
      frontendWebSocketProviderUrl: SEPOLIA_DEFAULT_WS_URL,
      backendProviderUrl: SEPOLIA_DEFAULT_URL,
      walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
      chainIcon: 'sepolia',

      nativeCurrency: {
        decimals: 18,
        name: 'Sepolia Ether',
        symbol: 'ETH',
      },
      contracts: {
        multicall3: {
          address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
          blockCreated: 751532,
        },
        ensRegistry: { address: `0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e` as `0x${string}` },
        ensUniversalResolver: {
          address: `0xc8Af999e38273D658BE1b921b88A9Ddf005769cC` as `0x${string}`,
          blockCreated: 5_317_080,
        },
      },
      isTestnet: true,
    }
  } else if (chainId === ChainId.PLUME_TESTNET) {
    return {
      chainId: ChainId.PLUME_TESTNET,
      chainName: 'Plume',
      blockExplorerUrl: BLOCK_EXPLORER_PLUME_TESTNET,
      etherscanUrl: ETHERSCAN_PLUME_TESTNET,
      chainIcon: 'plumeTestnet',

      nativeCurrency: {
        decimals: 18,
        name: 'Plume',
        symbol: 'PLUME',
      },
      contracts: {
        multicall3: {
          address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
          blockCreated: 7096959,
        },
      },
      isTestnet: true,
      frontendProviderUrl: PLUME_DEFAULT_TESTNET_URL,
      frontendWebSocketProviderUrl: PLUME_DEFAULT_TESTNET_WS_URL,
      backendProviderUrl: PLUME_DEFAULT_TESTNET_URL,
      walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
    }
  } else if (chainId === ChainId.AVALANCHE) {
    return {
      chainId: ChainId.AVALANCHE,
      chainName: 'Avalanche',
      blockExplorerUrl: BLOCK_EXPLORER_AVALANCHE,
      etherscanUrl: ETHERSCAN_AVALANCHE,
      frontendProviderUrl: AVALANCHE_DEFAULT_URL,
      frontendWebSocketProviderUrl: AVALANCHE_DEFAULT_WS_URL,
      backendProviderUrl: AVALANCHE_DEFAULT_URL,
      walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
      chainIcon: 'avalanche',

      nativeCurrency: {
        decimals: 18,
        name: 'Avalanche',
        symbol: 'AVAX',
      },
      contracts: {
        multicall3: {
          address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
          blockCreated: 11907934,
        },
      },
      isTestnet: false,
    }
  } else if (chainId === ChainId.PLUME_MAINNET) {
    return {
      chainId: ChainId.PLUME_MAINNET,
      chainName: 'Plume',
      blockExplorerUrl: BLOCK_EXPLORER_PLUME_MAINNET,
      etherscanUrl: ETHERSCAN_PLUME_MAINNET,
      frontendProviderUrl: PLUME_DEFAULT_MAINNET_URL,
      frontendWebSocketProviderUrl: PLUME_DEFAULT_MAINNET_WS_URL,
      backendProviderUrl: PLUME_DEFAULT_MAINNET_URL,
      walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
      chainIcon: 'plume',

      nativeCurrency: {
        decimals: 18,
        name: 'Plume',
        symbol: 'PLUME',
      },
      contracts: {
        multicall3: {
          address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
          blockCreated: 7096959,
        },
      },
      isTestnet: false,
    }
  } else if (chainId === ChainId.MAINNET) {
    return {
      chainId: ChainId.MAINNET,
      chainName: 'mainnet',
      blockExplorerUrl: BLOCK_EXPLORER_MAINNET,
      etherscanUrl: ETHERSCAN_MAINNET,
      frontendProviderUrl: MAINNET_DEFAULT_URL,
      frontendWebSocketProviderUrl: MAINNET_DEFAULT_WS_URL,
      backendProviderUrl: MAINNET_DEFAULT_URL,
      walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
      chainIcon: 'ethereum',

      nativeCurrency: {
        decimals: 18,
        name: 'Ether',
        symbol: 'ETH',
      },
      contracts: {
        multicall3: {
          address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
          blockCreated: 14353601,
        },
      },
      isTestnet: false,
    }
  } else if (chainId === ChainId.HARDHAT) {
    return {
      chainId: ChainId.HARDHAT,
      chainName: 'LocalHardhat',
      blockExplorerUrl: BLOCK_EXPLORER_SEPOLIA,
      etherscanUrl: ETHERSCAN_SEPOLIA,
      frontendProviderUrl: SEPOLIA_DEFAULT_URL,
      frontendWebSocketProviderUrl: SEPOLIA_DEFAULT_WS_URL,
      backendProviderUrl: SEPOLIA_DEFAULT_URL,
      walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
      chainIcon: 'hardhat',

      nativeCurrency: {
        decimals: 18,
        name: 'Sepolia Ether',
        symbol: 'ETH',
      },
      contracts: {
        multicall3: {
          address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
          blockCreated: 751532,
        },
        ensRegistry: { address: `0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e` as `0x${string}` },
        ensUniversalResolver: {
          address: `0xc8Af999e38273D658BE1b921b88A9Ddf005769cC` as `0x${string}`,
          blockCreated: 5_317_080,
        },
      },
      isTestnet: true,
    }
  }
  throw new Error(`chainIdToChainConfigurationValues: unknown chainId: ${chainId}`)
}

const CHAIN_CONFIG_FUJI: SimpleChainConfig = {
  name: 'fuji', // Avalanche Fuji Testnet
  chain: 'AVAX',
  chainID: ChainId.FUJI,
  rpcUrl: 'https://api.avax-test.network/ext/bc/C/rpc',
  //'https://rpc.gunzchain.io/ext/bc/2M47TxWHGnhNtq6pM5zPXdATBtuqubxn5EPFgFmEawCQr9WFML/rpc',
}

const CHAIN_CONFIG_SEPOLIA: SimpleChainConfig = {
  name: 'sepolia', // Ethereum Sepolia Testnet
  chain: 'ETH',
  chainID: ChainId.SEPOLIA,
  rpcUrl: 'https://rpc.sepolia.org',
}

// Plume Testnet
const CHAIN_CONFIG_PLUME: SimpleChainConfig = {
  name: 'plume', // Plume Testnet (use canonical if available)
  chain: 'PLUME',
  chainID: 98866,
  rpcUrl: 'https://rpc.plume.org',
}

// Base Sepolia
const CHAIN_CONFIG_BASE_SEPOLIA: SimpleChainConfig = {
  name: 'base-sepolia', // Base Sepolia Testnet
  chain: 'ETH',
  chainID: 84532,
  rpcUrl: 'https://sepolia.base.org',
}

// Ethereum Mainnet
const CHAIN_CONFIG_ETHEREUM: SimpleChainConfig = {
  name: 'mainnet',
  chain: 'ETH',
  chainID: 1,
  rpcUrl: 'https://eth.llamarpc.com',
}

// BNB Smart Chain Mainnet
const CHAIN_CONFIG_BNB: SimpleChainConfig = {
  name: 'bsc',
  chain: 'BNB',
  chainID: 56,
  rpcUrl: 'https://bsc-dataseed.binance.org',
}

// Base Mainnet
const CHAIN_CONFIG_BASE: SimpleChainConfig = {
  name: 'base',
  chain: 'ETH',
  chainID: 8453,
  rpcUrl: 'https://mainnet.base.org',
}

// Arbitrum One
const CHAIN_CONFIG_ARBITRUM: SimpleChainConfig = {
  name: 'arbitrum',
  chain: 'ETH',
  chainID: 42161,
  rpcUrl: 'https://arb1.arbitrum.io/rpc',
}

// Avalanche C-Chain Mainnet
const CHAIN_CONFIG_AVALANCHE: SimpleChainConfig = {
  name: 'avalanche',
  chain: 'AVAX',
  chainID: 43114,
  rpcUrl: 'https://api.avax.network/ext/bc/C/rpc',
}

// Plume Mainnet
const CHAIN_CONFIG_PLUME_MAINNET: SimpleChainConfig = {
  name: 'plume-mainnet',
  chain: 'PLUME',
  chainID: 161221136,
  rpcUrl: 'https://rpc.plume.org',
}

// Cronos Mainnet
const CHAIN_CONFIG_CRONOS: SimpleChainConfig = {
  name: 'cronos',
  chain: 'CRO',
  chainID: 25,
  rpcUrl: 'https://evm.cronos.org',
}

// Berachain Artio
const CHAIN_CONFIG_BERACHAIN: SimpleChainConfig = {
  name: 'berachain',
  chain: 'BERA',
  chainID: 80085,
  rpcUrl: 'https://artio.rpc.berachain.com',
}

export const testAssetChains: SimpleChainConfig[] = [
  CHAIN_CONFIG_FUJI,
  CHAIN_CONFIG_SEPOLIA,
  CHAIN_CONFIG_PLUME,
  CHAIN_CONFIG_BASE_SEPOLIA,
]

export const prodAssetChains: SimpleChainConfig[] = [
  CHAIN_CONFIG_ETHEREUM,
  CHAIN_CONFIG_BNB,
  CHAIN_CONFIG_BASE,
  CHAIN_CONFIG_ARBITRUM,
  CHAIN_CONFIG_ARBITRUM,
  CHAIN_CONFIG_AVALANCHE,
  CHAIN_CONFIG_PLUME_MAINNET,
  CHAIN_CONFIG_CRONOS,
  CHAIN_CONFIG_BERACHAIN,
]
